"use client";

import { useEffect, type ReactNode } from "react";
import { Box, Link, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { jetsProposal } from "../data/jets";
import { PROPOSAL_FONTS_HREF, proposalDisplay, proposalFont } from "../proposalTheme";

const proposal = jetsProposal;

const jets = {
  green: "#125740",
  greenDeep: "#0C3D2C",
  white: "#FFFFFF",
  paper: "#F3F6F4",
  ink: "#102019",
  muted: "#3C5146",
  line: "rgba(18, 87, 64, 0.16)",
  maxWidth: 1120,
};

export default function JetsProposalPage() {
  useEffect(() => {
    const existing = document.querySelector(`link[href="${PROPOSAL_FONTS_HREF}"]`);
    if (existing) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = PROPOSAL_FONTS_HREF;
    document.head.appendChild(link);
  }, []);

  return (
    <Box component="main" sx={{ bgcolor: jets.paper, color: jets.ink, minHeight: "100vh" }}>
      <Helmet>
        <title>New York Jets · A private night of comedy | ComedyUO</title>
        <meta
          name="description"
          content="A private comedy night for the New York Jets Partnerships team. Standout comedy, hospitality, and food and beverage in a New York room."
        />
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
        <meta property="og:title" content="New York Jets · A private night of comedy | ComedyUO" />
        <meta
          property="og:description"
          content="A private comedy night for the New York Jets Partnerships team."
        />
        <meta
          property="og:image"
          content="https://storage.googleapis.com/cuo-website/Jets%20Logo.jpg"
        />
        <meta property="og:image:alt" content="New York Jets" />
        <meta name="twitter:image" content="https://storage.googleapis.com/cuo-website/Jets%20Logo.jpg" />
      </Helmet>

      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 3,
          px: { xs: 3, md: 6 },
          height: { xs: 64, md: 76 },
          bgcolor: jets.green,
        }}
      >
        <Box
          component="img"
          src={proposal.comedyuoLogo}
          alt="ComedyUO"
          sx={{ height: { xs: 32, md: 40 }, width: "auto" }}
        />
        <Typography
          sx={{
            fontFamily: proposalFont,
            fontSize: { xs: "0.72rem", md: "0.8rem" },
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: jets.white,
          }}
        >
          New York Jets
        </Typography>
      </Box>

      <Box
        sx={{
          position: "relative",
          minHeight: { xs: "70vh", md: "calc(100vh - 76px)" },
          display: "flex",
          alignItems: "flex-end",
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src={proposal.heroImage}
          alt={proposal.heroImageAlt}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center 55%",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(12,61,44,0.2) 0%, rgba(8,32,24,0.35) 38%, rgba(8,24,18,0.88) 100%)",
          }}
        />
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            maxWidth: jets.maxWidth,
            mx: "auto",
            px: { xs: 3, md: 6 },
            pb: { xs: 6, md: 8 },
            pt: { xs: 8, md: 10 },
          }}
        >
          <Typography
            sx={{
              fontFamily: proposalFont,
              fontSize: "0.75rem",
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: jets.green,
              bgcolor: jets.white,
              display: "inline-block",
              px: 1.5,
              py: 0.75,
              mb: 2.5,
            }}
          >
            {proposal.heroEyebrow}
          </Typography>
          <Typography
            component="h1"
            sx={{
              fontFamily: proposalDisplay,
              fontWeight: 500,
              color: jets.white,
              fontSize: { xs: "2.75rem", md: "4.75rem" },
              lineHeight: 1.05,
              letterSpacing: "-0.03em",
              maxWidth: 760,
              mb: 2.5,
            }}
          >
            {proposal.heroHeadline}
          </Typography>
          <Typography
            sx={{
              fontFamily: proposalFont,
              fontSize: { xs: "1.05rem", md: "1.25rem" },
              lineHeight: 1.5,
              color: "rgba(255,255,255,0.92)",
              maxWidth: 540,
            }}
          >
            {proposal.heroSubheadline}
          </Typography>
        </Box>
      </Box>

      <Section tone="white">
        <Eyebrow>The evening</Eyebrow>
        <Heading>Your evening, taken care of.</Heading>
        <Body>{proposal.intro}</Body>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: { xs: 5, md: 4 },
            alignItems: "start",
          }}
        >
          {proposal.pillars.map((pillar) => (
            <Box key={pillar.number}>
              <Box
                component="img"
                src={pillar.image}
                alt={pillar.imageAlt}
                sx={{
                  width: "100%",
                  height: { xs: 220, md: 260 },
                  objectFit: "cover",
                  mb: 2.5,
                  display: "block",
                }}
              />
              <Typography
                sx={{
                  fontFamily: proposalFont,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  color: jets.green,
                  mb: 1,
                }}
              >
                {pillar.number}
              </Typography>
              <Typography
                sx={{
                  fontFamily: proposalDisplay,
                  fontSize: { xs: "1.6rem", md: "1.85rem" },
                  lineHeight: 1.1,
                  color: jets.ink,
                  mb: 1.25,
                }}
              >
                {pillar.title}
              </Typography>
              <Typography
                sx={{
                  fontFamily: proposalFont,
                  color: jets.muted,
                  fontSize: "1rem",
                  lineHeight: 1.6,
                }}
              >
                <LinkedCopy text={pillar.body} />
              </Typography>
            </Box>
          ))}
        </Box>
      </Section>

      <Section tone="white">
        <Eyebrow>Venue shortlist</Eyebrow>
        <Heading>Five options.</Heading>
        <Body>
          Photos are from ComedyUO nights at these venues. Cards link to Google Drive when we have show photos. The Seville links to their Instagram.
        </Body>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 3, md: 4 },
          }}
        >
          {proposal.venues.map((venue) => (
            <Box
              key={venue.name}
              id={`venue-${venue.id}`}
              sx={{
                bgcolor: jets.white,
                border: `1px solid ${jets.line}`,
                height: "100%",
                scrollMarginTop: { xs: "80px", md: "96px" },
              }}
            >
              {venue.image ? (
                <Box
                  component="img"
                  src={venue.image}
                  alt={venue.imageAlt}
                  sx={{ width: "100%", height: { xs: 220, md: 280 }, objectFit: "cover", display: "block" }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: { xs: 220, md: 280 },
                    bgcolor: jets.green,
                    display: "flex",
                    alignItems: "flex-end",
                    px: 3,
                    py: 3,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: proposalDisplay,
                      color: jets.white,
                      fontSize: { xs: "2rem", md: "2.4rem" },
                      lineHeight: 1.05,
                    }}
                  >
                    {venue.name}
                  </Typography>
                </Box>
              )}
              <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
                <Typography
                  sx={{
                    fontFamily: proposalFont,
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: jets.green,
                    mb: 1,
                  }}
                >
                  {venue.when} · {venue.neighborhood}
                </Typography>
                <Typography sx={{ fontFamily: proposalDisplay, fontSize: { xs: "1.75rem", md: "2rem" }, lineHeight: 1.1, color: jets.ink, mb: 1.25 }}>{venue.name}</Typography>
                <Typography sx={{ fontFamily: proposalFont, color: jets.muted, fontSize: "1rem", lineHeight: 1.6, mb: 2.5 }}>
                  {venue.body}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
                  {venue.links.map((link) => (
                    <Link
                      key={link.href}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="none"
                      sx={{
                        fontFamily: proposalFont,
                        fontSize: "0.85rem",
                        fontWeight: 700,
                        color: jets.white,
                        bgcolor: jets.green,
                        px: 1.5,
                        py: 0.85,
                        "&:hover": { bgcolor: jets.greenDeep },
                      }}
                    >
                      {link.label}
                    </Link>
                  ))}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Section>

      <Section tone="green">
        <Eyebrow onGreen>Run of show</Eyebrow>
        <Heading onGreen>How the night moves.</Heading>
        <Body onGreen>About two and a half hours. Times flex to the room and the guest list.</Body>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {proposal.runOfShow.map((step, i) => (
            <Box
              key={step.title}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "96px 1fr", md: "140px 1fr" },
                gap: { xs: 2, md: 4 },
                py: { xs: 2.75, md: 3.25 },
                borderTop: "1px solid rgba(255,255,255,0.22)",
                borderBottom:
                  i === proposal.runOfShow.length - 1 ? "1px solid rgba(255,255,255,0.22)" : "none",
              }}
            >
              <Typography
                sx={{
                  fontFamily: proposalFont,
                  fontWeight: 700,
                  fontSize: { xs: "1rem", md: "1.15rem" },
                  color: jets.white,
                  pt: 0.4,
                }}
              >
                {step.time}
              </Typography>
              <Box>
                <Typography
                  sx={{
                    fontFamily: proposalDisplay,
                    fontSize: { xs: "1.45rem", md: "1.75rem" },
                    lineHeight: 1.15,
                    color: jets.white,
                    mb: 0.75,
                  }}
                >
                  {step.title}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: proposalFont,
                    color: "rgba(255,255,255,0.82)",
                    fontSize: "1.02rem",
                    lineHeight: 1.55,
                    maxWidth: 640,
                  }}
                >
                  {step.body}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Section>

      <Section tone="paper">
        <Eyebrow>Production</Eyebrow>
        <Heading>You host the clients. We produce the night.</Heading>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: { xs: 1.5, md: 2 },
            maxWidth: 760,
          }}
        >
          {proposal.handles.map((item) => (
            <Box
              key={item}
              sx={{
                bgcolor: jets.white,
                borderLeft: `4px solid ${jets.green}`,
                px: 2.25,
                py: 1.75,
              }}
            >
              <Typography sx={{ fontFamily: proposalFont, fontSize: "1.02rem", color: jets.ink, lineHeight: 1.4 }}>
                {item}
              </Typography>
            </Box>
          ))}
        </Box>
      </Section>

      <Section tone="white">
        <Eyebrow>Next</Eyebrow>
        <Heading>Share a date window, a guest count, and a room.</Heading>
        <Body>
          We will prepare a tailored quote and a hospitality plan, including food and beverage if you want it.
          All of these experiences have a floor of $10,000.
          All venues and talent are subject to availability. Final pricing follows the date, guest count, venue, and hospitality scope.
        </Body>
        <Typography sx={{ fontFamily: proposalDisplay, fontSize: { xs: "1.6rem", md: "1.85rem" }, color: jets.ink, mb: 0.75 }}>
          {proposal.contactName}
        </Typography>
        <Typography sx={{ fontFamily: proposalFont, color: jets.muted, mb: 2 }}>
          {proposal.contactRole}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          <Link
            href={`mailto:${proposal.contactEmail}?subject=${encodeURIComponent("New York Jets private comedy night")}`}
            underline="none"
            sx={{
              fontFamily: proposalFont,
              fontWeight: 700,
              color: jets.white,
              bgcolor: jets.green,
              px: 2,
              py: 1.15,
              "&:hover": { bgcolor: jets.greenDeep },
            }}
          >
            {proposal.contactEmail}
          </Link>
          <Link
            href="tel:+13104698961"
            underline="none"
            sx={{
              fontFamily: proposalFont,
              fontWeight: 700,
              color: jets.green,
              border: `1px solid ${jets.green}`,
              px: 2,
              py: 1.15,
              "&:hover": { bgcolor: "rgba(18,87,64,0.06)" },
            }}
          >
            {proposal.contactPhone}
          </Link>
        </Box>
      </Section>

      <Box sx={{ px: { xs: 3, md: 6 }, py: 3.5, bgcolor: jets.greenDeep }}>
        <Typography sx={{ fontFamily: proposalFont, fontSize: "0.85rem", color: "rgba(255,255,255,0.82)" }}>
          Prepared by ComedyUO · Confidential · New York Jets
        </Typography>
      </Box>
    </Box>
  );
}

const VENUE_ANCHORS = [
  { label: "13th Storey", id: "venue-13th-storey" },
  { label: "Scuba Network", id: "venue-scuba-network" },
  { label: "The Seville", id: "venue-seville" },
  { label: "Paragon Sports", id: "venue-paragon" },
  { label: "Zabar's", id: "venue-zabars" },
  { label: "Paragon", id: "venue-paragon" },
].sort((a, b) => b.label.length - a.label.length);

function LinkedCopy({ text }: { text: string }) {
  const pattern = new RegExp(
    `(${VENUE_ANCHORS.map((venue) => venue.label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`,
    "g",
  );
  const parts = text.split(pattern);
  return (
    <>
      {parts.map((part, index) => {
        const venue = VENUE_ANCHORS.find((item) => item.label === part);
        if (!venue) return part;
        return (
          <Link
            key={`${venue.id}-${index}`}
            href={`#${venue.id}`}
            underline="always"
            onClick={(event) => {
              event.preventDefault();
              document.getElementById(venue.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            sx={{ color: jets.green, fontWeight: 700 }}
          >
            {part}
          </Link>
        );
      })}
    </>
  );
}

function Section({
  children,
  tone = "paper",
}: {
  children: ReactNode;
  tone?: "white" | "paper" | "green";
}) {
  const bg = tone === "green" ? jets.green : tone === "white" ? jets.white : jets.paper;
  return (
    <Box
      component="section"
      sx={{
        bgcolor: bg,
        px: { xs: 3, md: 6 },
        py: { xs: 7, md: 11 },
      }}
    >
      <Box sx={{ maxWidth: jets.maxWidth, mx: "auto" }}>{children}</Box>
    </Box>
  );
}

function Eyebrow({ children, onGreen = false }: { children: ReactNode; onGreen?: boolean }) {
  return (
    <Typography
      sx={{
        fontFamily: proposalFont,
        fontSize: "0.78rem",
        fontWeight: 700,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color: onGreen ? "rgba(255,255,255,0.82)" : jets.green,
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );
}

function Heading({ children, onGreen = false }: { children: ReactNode; onGreen?: boolean }) {
  return (
    <Typography
      component="h2"
      sx={{
        fontFamily: proposalDisplay,
        fontWeight: 500,
        fontSize: { xs: "2.15rem", md: "3rem" },
        lineHeight: 1.05,
        letterSpacing: "-0.03em",
        color: onGreen ? jets.white : jets.ink,
        mb: 2,
        maxWidth: 740,
      }}
    >
      {children}
    </Typography>
  );
}

function Body({ children, onGreen = false }: { children: ReactNode; onGreen?: boolean }) {
  return (
    <Typography
      sx={{
        fontFamily: proposalFont,
        fontSize: { xs: "1.05rem", md: "1.125rem" },
        lineHeight: 1.6,
        color: onGreen ? "rgba(255,255,255,0.86)" : jets.muted,
        maxWidth: 680,
        mb: 5,
      }}
    >
      {children}
    </Typography>
  );
}
